import { useMutation, useQuery } from "@tanstack/react-query"
import { AddressPerson, BankAccountPerson, Custom, HealthPerson, JuridicPerson, Profile } from "@/types/person"
import { personService } from "../end-points/person"
import { toast } from "@/hooks/use-toast"

// ============================================
// QUERY KEY FACTORY
// ============================================
export const personKeys = {
  all: ['persons'] as const,
  searches: () => [...personKeys.all, 'search'] as const,
  search: (query: string) => [...personKeys.searches(), { query }] as const,
};

// ============================================
// QUERY HOOKS
// ============================================

/**
 * Search persons by name, email, or CPF
 * @param query - Search query string
 */
export const useSearchPersons = (query: string) => {
  return useQuery({
    queryKey: personKeys.search(query),
    queryFn: () => personService.searchPersons(query),
    // enabled: query.length >= 3, // Only search with 3+ characters
    staleTime: 30 * 1000, // 30 seconds
  });
};

// ============================================
// MUTATION HOOKS
// ============================================

export const useUpdateProfile = (showToast: boolean = true) => {
  return useMutation({
    mutationFn: (data: Profile) => personService.updateProfile(data),
    onSuccess: () => {
      if (showToast) {
        toast({
          title: "Perfil atualizado",
          description: "Perfil atualizado com sucesso",
          variant: "success",
        })
      }
    },
    onError: (err) => {
      console.log(err)
      if (showToast) {
        toast({
          title: "Erro ao atualizar perfil",
          description: "Não foi possível atualizar o perfil",
          variant: "destructive",
        })
      }
    }
  })
}

export const useRegisterAddress = (showToast: boolean = true) => {
  return useMutation({
    mutationFn: (data: AddressPerson) => personService.registerAddress(data),
    onSuccess: () => {
      if (showToast) {
        toast({
          title: "Endereço registrado",
          description: "Endereço registrado com sucesso",
          variant: "success",
        })
      }
    },
    onError: (err) => {
      console.log(err)
      if (showToast) {
        toast({
          title: "Erro ao registrar endereço",
          description: "Não foi possível registrar o endereço",
          variant: "destructive",
        })
      }
    }
  })
}

export const useUpdateAddress = (showToast: boolean = true) => {
  return useMutation({
    mutationFn: (data: AddressPerson) => personService.updateAddress(data),
    onSuccess: () => {
      if (showToast) {
        toast({
          title: "Endereço atualizado",
          description: "Endereço atualizado com sucesso",
          variant: "success",
        })
      }
    },
    onError: (err) => {
      console.log(err)
      if (showToast) {
        toast({
          title: "Erro ao atualizar endereço",
          description: "Não foi possível atualizar o endereço",
          variant: "destructive",
        })
      }
    }
  })
}

export const useUpdateHealth = (showToast: boolean = true) => {
  return useMutation({
    mutationFn: (data: HealthPerson) => personService.updateHealth(data),
    onSuccess: () => {
      if (showToast) {
        toast({
          title: "Dados de saúde atualizados",
          description: "Dados de saúde atualizados com sucesso",
          variant: "success",
        })
      }
    },
    onError: (err) => {
      console.log(err)
      if (showToast) {
        toast({
          title: "Erro ao atualizar dados de saúde",
          description: "Não foi possível atualizar os dados de saúde",
          variant: "destructive",
        })
      }
    }
  })
}

export const useRegisterBankDetails = (showToast: boolean = true) => {
  return useMutation({
    mutationFn: (data: BankAccountPerson) => personService.registerBankDetails(data),
    onSuccess: () => {
      if (showToast) {
        toast({
          title: "Dados bancários registrados",
          description: "Dados bancários registrados com sucesso",
          variant: "success",
        })
      }
    },
    onError: (err) => {
      console.log(err)
      if (showToast) {
        toast({
          title: "Erro ao registrar dados bancários",
          description: "Não foi possível registrar os dados bancários",
          variant: "destructive",
        })
      }
    }
  })
}

export const useUpdateBankDetails = (showToast: boolean = true) => {
  return useMutation({
    mutationFn: (data: BankAccountPerson) => personService.updateBankDetails(data),
    onSuccess: () => {
      if (showToast) {
        toast({
          title: "Dados bancários atualizados",
          description: "Dados bancários atualizados com sucesso",
          variant: "success",
        })
      }
    },
    onError: (err) => {
      console.log(err)
      if (showToast) {
        toast({
          title: "Erro ao atualizar dados bancários",
          description: "Não foi possível atualizar os dados bancários",
          variant: "destructive",
        })
      }
    }
  })
}

export const useUpdateJuridicPerson = (showToast: boolean = true) => {
  return useMutation({
    mutationFn: (data: JuridicPerson) => personService.updateJuridicPerson(data),
    onSuccess: () => {
      if (showToast) {
        toast({
          title: "Pessoa jurídica atualizada",
          description: "Pessoa jurídica atualizada com sucesso",
          variant: "success",
        })
      }
    },
    onError: (err) => {
      console.log(err)
      if (showToast) {
        toast({
          title: "Erro ao atualizar pessoa jurídica",
          description: "Não foi possível atualizar a pessoa jurídica",
          variant: "destructive",
        })
      }
    }
  })
}

export const useUploadDoc = (showToast: boolean = true) => {
  return useMutation({
    mutationFn: ({ data }: { data: FormData }) => personService.uploadDoc(data),
    onSuccess: () => {
      if (showToast) {
        toast({
          title: "Documento enviado",
          description: "Documento enviado com sucesso",
          variant: "success",
        })
      }
    },
    onError: (err) => {
      console.log(err)
      if (showToast) {
        toast({
          title: "Erro ao enviar documento",
          description: "Não foi possível enviar o documento",
          variant: "destructive",
        })
      }
    }
  })
}

export const usePersonCustom = (showToast: boolean = true) => {
  return useMutation({
    mutationFn: (data: Custom) => personService.updateCustom(data),
    onSuccess: () => {
      if (showToast) {
        toast({
          title: "Dados customizados atualizados",
          description: "Dados customizados atualizados com sucesso",
          variant: "success",
        })
      }
    },
    onError: (err) => {
      console.log(err)
      if (showToast) {
        toast({
          title: "Erro ao atualizar dados customizados",
          description: "Não foi possível atualizar os dados customizados",
          variant: "destructive",
        })
      }
    }
  })
}

export const usePersonMe = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['person-me'],
    queryFn: () => personService.me(),
    staleTime: 1000 * 60 * 60, // 1 hora
    enabled: options?.enabled,
  })
}